import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import GenericTable from 'shared/components/GenericTable/genericTable';
import { useGenerateUuid } from 'shared/utils/useGenerateUuid';
import { AppEvents, emit$ } from 'shared/services/app-events';
import SoftTypography from 'components/SoftTypography';
import SoftButton from 'components/SoftButton';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Box } from '@mui/material';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import useAgencyService from './useAgencyService';
import styles from './agency-manager.module.scss';
import ErrorLoggingService from '../../../shared/services/errorlogging.service';
import SearchField from 'shared/components/SearchField/SearchField';
import { LoaderContext } from 'shared/context/loaderContext';
import SoftBox from '../../../components/SoftBox';
import { UserDataContext } from 'shared/context/userDataContext';
import { paymentMethodValues } from './agency-details/agency-details-form';

function AgencyList() {
  const { userRoles } = useContext(UserDataContext);
  const { setLoading } = useContext(LoaderContext);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const tableUuid = useGenerateUuid();
  const errorLoggingService = ErrorLoggingService();
  const { getAgencies } = useAgencyService();
  const [agencies, setAgencies] = useState([]);
  const [filteredAgencies, setFilteredAgencies] = useState([]);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [actionsMenuEl, setActionsMenuEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const isActionsMenuOpen = Boolean(actionsMenuEl);

  const fetchAgencies = async () => {
    try {
      setLoading(true);

      const fetchedAgencies = await getAgencies();

      setAgencies(fetchedAgencies);
      setFilteredAgencies(fetchedAgencies);
    } catch (err) {
      errorLoggingService.log('Error fetching agencies:', err);

      setError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    emit$(AppEvents.RESET_TABLE_COUNTER, tableUuid);

    if (!searchTerm.trim()) {
      setFilteredAgencies(agencies);
      return;
    }

    const lowerTerm = searchTerm.toLowerCase();
    const filtered = agencies.filter((agency) => {
      const matchFields = [agency.identifier, agency.shopName, agency.email];

      return matchFields.some(
        (field) => field && field.toLowerCase().includes(lowerTerm),
      );
    });

    setFilteredAgencies(filtered);
  };

  const handleAddNewAgency = () => navigate('/agency-details/new');

  const handleActionsMenuClick = (e, rowValue) => {
    e.stopPropagation();
    setActionsMenuEl(e.currentTarget);
    setSelectedRow(rowValue);
  };

  const closeActionsMenu = (e) => {
    if (e) e.stopPropagation();
    setActionsMenuEl(null);
    setSelectedRow(null);
  };

  const executeActionAndCloseMenu = (e, action) => {
    if (e) e.stopPropagation();
    action(selectedRow);
    closeActionsMenu();
  };

  const openDetails = (agency) =>
    navigate(`/agency-details/${agency.identifier}`);

  useEffect(() => {
    fetchAgencies();
  }, []);

  const columns = [
    {
      label: t('AGENCY_MANAGER.AGENCIES_LIST.COLUMNS.agencyId'),
      value: (agency) => agency.identifier,
    },
    {
      label: t('AGENCY_MANAGER.AGENCIES_LIST.COLUMNS.agencyName'),
      value: (agency) => agency.shopName,
    },
    {
      label: t('AGENCY_MANAGER.AGENCIES_LIST.COLUMNS.country'),
      value: (agency) => agency.country.toUpperCase(),
    },
    {
      label: t('AGENCY_MANAGER.AGENCIES_LIST.COLUMNS.brand'),
      value: (agency) => agency.brands.join(', '),
    },
    {
      label: t('AGENCY_MANAGER.AGENCIES_LIST.COLUMNS.abn'),
      value: (agency) => agency.abn,
    },
    {
      label: t('AGENCY_MANAGER.AGENCIES_LIST.COLUMNS.email'),
      value: (agency) => agency.email,
    },
    {
      label: t('AGENCY_MANAGER.AGENCIES_LIST.COLUMNS.phoneNumber'),
      value: (agency) => agency.phoneNumber,
    },
    {
      label: t('AGENCY_MANAGER.AGENCIES_LIST.COLUMNS.distributionAgreement'),
      value: (agency) =>
        agency.isAgreementSigned ? t('COMMON.yes') : t('COMMON.no'),
    },
    {
      label: t('AGENCY_MANAGER.AGENCIES_LIST.COLUMNS.trusted'),
      value: (agency) => (agency.isTrusted ? t('COMMON.yes') : t('COMMON.no')),
    },
    {
      label: t('AGENCY_MANAGER.AGENCIES_LIST.COLUMNS.paymentMethod'),
      value: (agency) =>
        paymentMethodValues.find(
          (paymentMethod) => paymentMethod.value === agency.paymentMethod,
        )?.label || agency.paymentMethod,
    },
    {
      label: t('AGENCY_MANAGER.AGENCIES_LIST.COLUMNS.merchantOfRecord'),
      value: (agency) => agency.merchantOfRecord,
    },
    {
      label: t('AGENCY_MANAGER.AGENCIES_LIST.COLUMNS.actions'),
      value: (rowValue) => (
        <div className={styles.actionsColumn}>
          <SoftButton
            variant="outlined"
            color="secondary"
            onClick={(e) => handleActionsMenuClick(e, rowValue)}
            className={styles.actionButton}
          >
            {t('AGENCY_MANAGER.AGENCIES_LIST.COLUMNS.actions')}
            <KeyboardArrowDownIcon className={styles.actionIcon} />
          </SoftButton>
          <Menu
            elevation={0}
            anchorEl={actionsMenuEl}
            open={isActionsMenuOpen}
            onClose={closeActionsMenu}
            classes={{ paper: styles.customMenuPaper }}
          >
            <MenuItem
              onClick={(e) => executeActionAndCloseMenu(e, openDetails)}
            >
              {t('AGENCY_MANAGER.AGENCIES_LIST.viewAgencyDetails')}
            </MenuItem>
          </Menu>
        </div>
      ),
    },
  ];

  if (error)
    return (
      <div>
        <Box className={styles.topContainer}>
          <SoftBox
            sx={{
              marginTop: '6rem',
            }}
          >
            <SoftTypography
              variant="h2"
              fontWeight="bold"
              sx={{ marginBottom: '2rem' }}
            >
              {t('AGENCY_MANAGER.AGENCIES_LIST.title')}
            </SoftTypography>
          </SoftBox>
          <p>
            {t('AGENCY_MANAGER.AGENCIES_LIST.errorLoading')}{' '}
            {error?.message || ''}
          </p>
        </Box>
      </div>
    );

  return (
    <div>
      <Box className={styles.topContainer}>
        <SoftBox
          sx={{
            marginTop: '6rem',
          }}
        >
          <SoftTypography
            variant="h2"
            fontWeight="bold"
            sx={{ marginBottom: '2rem' }}
          >
            {t('AGENCY_MANAGER.AGENCIES_LIST.title')}
          </SoftTypography>
        </SoftBox>

        <SearchField
          searchFunc={handleSearch}
          searchCriteria={searchTerm}
          onSearchCriteriaChange={(event) => setSearchTerm(event.target.value)}
          inputLabel={t('AGENCY_MANAGER.AGENCIES_LIST.searchLabel')}
          inputPlaceholder={t('AGENCY_MANAGER.AGENCIES_LIST.searchPlaceholder')}
          buttonText={t('COMMON.search')}
          containerStyles={styles.searchContainer}
          inputStyles={styles.searchInputs}
        />
      </Box>
      <GenericTable
        tableUuid={tableUuid}
        data={filteredAgencies || agencies}
        columns={columns}
        path="AGENCIES"
        onRowClick={openDetails}
      />
      {userRoles.t3Modify && (
        <Box className={styles.actionsLeft}>
          <SoftButton
            variant="contained"
            color="info"
            onClick={handleAddNewAgency}
          >
            {t('AGENCY_MANAGER.AGENCIES_LIST.addNewAgency')}
          </SoftButton>
        </Box>
      )}
    </div>
  );
}
export default AgencyList;
